import { useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { Request } from 'commons/utils/request'
import { Toast } from 'ui/components'
import { CreateClientUserRequest, EditClientUserRequest } from '~bank-client-users/types'

import { CreateClientUserFormType, EditClientUserFormType } from '../types'

export const useUserFormRequest = (
  closeEditClientUserDialog: () => void,
  reloadClientUserList?: () => void,
  clientUserForEditingId?: string,
  handlerFormError?: (error: any) => void
) => {
  const [isSubmittingClientUserForm, setIsSubmittingClientUserForm] = useState<boolean>(false)

  const { mutate: sendCreateClientUser } = useMutation<void, Error, CreateClientUserRequest>({
    mutationFn: (opts) => Request.post(Request.urls.bank.createClientUser, opts),
    onSuccess: async () => {
      Toast.successSmall('Client user was successfully created!')
      if (reloadClientUserList) {
        reloadClientUserList()
      }
      setIsSubmittingClientUserForm(false)
      closeEditClientUserDialog()
    },
    onError: (error: any) => {
      setIsSubmittingClientUserForm(false)
      if (handlerFormError) {
        handlerFormError(error)
      }
    }
  })

  const createClientUser = (item: CreateClientUserFormType) => {
    const { firstName, lastName, email, phone } = item
    setIsSubmittingClientUserForm(true)
    if (firstName && lastName && email) {
      sendCreateClientUser({
        firstName,
        lastName,
        email,
        phone,
        assignedRelationships: item.assignedRelationships,
        assignedPermissions: [...Object.keys(item).filter((k) => item[k] === true)]
      })
    }
  }

  const { mutate: sendEditClientUser } = useMutation<void, Error, EditClientUserRequest>({
    mutationFn: (opts) => Request.post(Request.urls.bank.editClientUser, opts),
    onSuccess: async () => {
      Toast.successSmall('Client user was successfully edited!')
      if (reloadClientUserList) {
        reloadClientUserList()
      }
      setIsSubmittingClientUserForm(false)
      closeEditClientUserDialog()
    },
    onError: (error) => {
      setIsSubmittingClientUserForm(false)
      if (handlerFormError) {
        handlerFormError(error)
      }
    }
  })

  const editClientUser = (item: EditClientUserFormType) => {
    setIsSubmittingClientUserForm(true)
    if (clientUserForEditingId && item.assignedRelationships) {
      sendEditClientUser({
        id: clientUserForEditingId,
        assignedRelationships: item.assignedRelationships,
        assignedPermissions: [...Object.keys(item).filter((k) => item[k] === true)]
      })
    }
  }

  return {
    isSubmittingClientUserForm,
    createClientUser,
    editClientUser
  }
}
